// src/components/PhotoCaptureQR.js
// ESTE ARCHIVO YA ERA CORRECTO. La corrección necesaria estaba en 'services/api.js'.
// +++ INICIO: CIERRE AUTOMÁTICO DE MODAL +++
// Se mejora la lógica de sondeo (polling) para que al detectar un error 404
// (sesión no encontrada), se cierre el modal automáticamente.
// +++ FIN: CIERRE AUTOMÁTICO DE MODAL +++

import React, { useState, useEffect, useRef } from 'react';
import { Modal, Spin, Alert, Typography, message, Button, Tooltip } from 'antd';
import { QRCodeSVG } from 'qrcode.react';
import moment from 'moment';
import { deliveryService } from '../services/api';

const { Text, Paragraph } = Typography;

const PhotoCaptureQR = ({ visible, onClose, docEntry, onComplete, formatoListado = 'A' }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [qrValue, setQrValue] = useState('');
  const pollIntervalRef = useRef(null);

  const handleClose = () => {
    stopPolling();
    if (onClose) {
      onClose();
    }
  };

  const createPhotoSession = async () => {
    if (!docEntry) {
      setError("No se proporcionó DocEntry para crear la sesión.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setSessionData(null);
    setQrValue('');

    try {
      // Esta llamada ya pasaba correctamente 'formatoListado' al servicio.
      const response = await deliveryService.createPhotoSession(docEntry, formatoListado);

      if (response.data.success) {
        const { sessionId, expiresAt } = response.data.data;
        setSessionData({ sessionId, expiresAt });

        const publicUrl = process.env.REACT_APP_PUBLIC_URL;
        const localIp = process.env.REACT_APP_SERVER_IP || window.location.hostname;
        const localPort = window.location.port || '';
        const localProtocol = window.location.protocol;

        const baseUrl = publicUrl ? publicUrl.replace(/\/+$/, '') : `${localProtocol}//${localIp}${localPort ? ':' + localPort : ''}`;
        const mobileUrl = `${baseUrl}/mobile-capture/${sessionId}`;

        console.log("DEBUG PhotoCaptureQR: URL para QR:", mobileUrl, "Formato:", formatoListado);
        setQrValue(mobileUrl);
        startPolling(sessionId);

      } else {
        setError(response.data.message || "Error al crear la sesión en el backend.");
      }
    } catch (err) {
      console.error("Error creando sesión:", err);
      setError(err.response?.data?.message || "Error de red o servidor al crear la sesión.");
    } finally {
      setLoading(false);
    }
  };

  const checkSessionStatus = async (sessionId) => {
    if (!sessionId) return;
    try {
      const response = await deliveryService.getSessionStatus(sessionId);
      if (response.data.success) {
        const statusData = response.data.data;
        if (statusData.status === 'photos_completed') {
          stopPolling();
          message.success("Fotos recibidas correctamente!");
          if (onComplete) {
            onComplete(statusData.photos || []);
          }
        }
        const now = new Date();
        if (statusData.expiresAt && new Date(statusData.expiresAt) < now) {
            stopPolling();
            setError("La sesión ha expirado.");
        }
      }
    } catch (error) {
      // +++ INICIO: LÓGICA DE CIERRE AUTOMÁTICO +++
      // Si el servidor devuelve 404, significa que la sesión fue cerrada o cancelada.
      if (error.response?.status === 404) {
          console.log("Polling: Sesión no encontrada (probablemente cerrada o cancelada). Cerrando modal.");
          stopPolling();
          // Llama a la función onComplete para que el editor se actualice,
          // o simplemente cierra el modal si onComplete no está disponible.
          if (onComplete) {
            onComplete(); 
          } else {
            handleClose();
          }
      } else {
        console.error("Error en polling:", error);
      }
      // +++ FIN: LÓGICA DE CIERRE AUTOMÁTICO +++
    }
  };

  const startPolling = (sessionId) => {
      stopPolling();
      if (sessionId) {
          checkSessionStatus(sessionId);
          pollIntervalRef.current = setInterval(() => checkSessionStatus(sessionId), 5000);
      }
  };

  const stopPolling = () => {
      if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
      }
  };

  useEffect(() => {
    if (visible && docEntry) {
      createPhotoSession();
    } else if (!visible) {
      stopPolling();
    }
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, docEntry, formatoListado]);

  return (
    <Modal
      title="Capturar Evidencias con Móvil"
      open={visible}
      onCancel={handleClose}
      footer={[
        <Button key="close" onClick={handleClose}>
          Cerrar
        </Button>,
      ]}
      width={400}
    >
      {loading && (
        <div style={{ textAlign: 'center', padding: '30px' }}>
          <Spin />
          <span style={{ marginLeft: 8 }}>Generando sesión y código QR...</span>
        </div>
      )}
      {error && (
        <Alert message="Error" description={error} type="error" showIcon />
      )}
      {!loading && !error && qrValue && sessionData && (
        <div style={{ textAlign: 'center' }}>
          <Paragraph>
            Escanee el siguiente código QR con la cámara de su dispositivo móvil para iniciar la captura de fotos.
          </Paragraph>
          <div style={{ margin: '20px 0', display: 'inline-block', background: 'white', padding: '10px', border: '1px solid #eee' }}>
            <QRCodeSVG value={qrValue} size={256} level="M" includeMargin={false} />
          </div>
          <Paragraph copyable={{ text: qrValue }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>O abra esta URL en su móvil:</Text><br/>
            <Text code style={{ fontSize: '11px' }}>{qrValue}</Text>
          </Paragraph>
          {sessionData.expiresAt && (
             <Paragraph type="secondary" style={{ fontSize: '12px', marginTop: '10px' }}>
                La sesión expira: {moment(sessionData.expiresAt).format('DD/MM/YYYY HH:mm:ss')}
                ({moment(sessionData.expiresAt).fromNow()})
             </Paragraph>
          )}
           <Paragraph style={{ marginTop: '15px' }}>
               <Spin size="small" style={{marginRight: '8px'}} /> Esperando fotos... (Actualiza automáticamente)
           </Paragraph>
        </div>
      )}
    </Modal>
  );
};

export default PhotoCaptureQR;
